import type React from 'react';

import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { LogOut, Lock, Users, Home, ChevronRight, ChevronLeft, ClipboardList } from 'lucide-react';
import { navPath } from '../../../constant';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/pages/dashboard/components/theme-toggle';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '@/components/ui/sheet';

interface SidebarProps {
  collapsed?: boolean;
  setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>;
  isMobileOpen?: boolean;
  setIsMobileOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

type NavigationItem = {
  title: string;
  href: string;
  icon: React.ElementType;
  isAdminOnly?: boolean;
  isActive?: boolean;
};

const NavLink = ({
  item,
  collapsed,
  isActive,
  onClick,
}: {
  item: NavigationItem;
  collapsed: boolean;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <Button
      variant={isActive ? 'secondary' : 'ghost'}
      className={`w-full h-9 ${collapsed ? 'px-0 justify-center' : 'px-3 justify-start'}`}
      onClick={onClick}
    >
      <item.icon className={`h-4 w-4 ${collapsed ? '' : 'mr-2'}`} />
      {!collapsed && <span>{item.title}</span>}
    </Button>
  );
};

const Sidebar = ({
  collapsed: propCollapsed,
  setCollapsed: propSetCollapsed,
  isMobileOpen = false,
  setIsMobileOpen,
}: SidebarProps) => {
  const navigate = useNavigate();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const collapsed = propCollapsed !== undefined ? propCollapsed : internalCollapsed;
  const setCollapsed = propSetCollapsed || setInternalCollapsed;

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const closeMobileMenu = () => {
    if (setIsMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('role');
      localStorage.removeItem('username');

      navigate(navPath.login);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out. Please try again.');
    }
  };

  const mainNavItems: NavigationItem[] = [
    {
      title: 'Monitor',
      href: navPath.dashboard.monitor.admin,
      icon: Home,
      isAdminOnly: true,
      isActive: window.location.pathname === navPath.dashboard.monitor.admin,
    },
    {
      title: 'Changelog',
      href: navPath.dashboard.changelog,
      icon: ClipboardList,
      isAdminOnly: true,
      isActive: window.location.pathname === navPath.dashboard.changelog,
    },
  ];

  const settingsNavItems: NavigationItem[] = [
    {
      title: 'Reset Password',
      href: navPath.dashboard.resetPassword,
      icon: Lock,
      isActive: window.location.pathname === navPath.dashboard.resetPassword,
    },
    {
      title: 'User Management',
      href: navPath.dashboard.userPanel,
      icon: Users,
      isAdminOnly: true,
      isActive: window.location.pathname === navPath.dashboard.userPanel,
    },
  ];

  const SidebarContent = () => (
    <>
      <div className={`flex h-16 items-center border-b ${collapsed && !isMobile ? 'justify-center' : 'px-4 justify-between'}`}>
        {(!collapsed || isMobile) && (
          <div className='flex items-center gap-2'>
            <span className='text-lg font-semibold whitespace-nowrap'>Dashboard</span>
          </div>
        )}

        <Button
          variant='ghost'
          size='icon'
          onClick={isMobile ? closeMobileMenu : toggleSidebar}
          className={`hidden lg:flex ${collapsed ? 'w-full px-0 mx-2 justify-center' : ''}`}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={20} />}
        </Button>
      </div>

      <nav className='flex flex-col flex-1 overflow-y-auto p-2 space-y-4'>
        <div className='space-y-1'>
          {(!collapsed || isMobile) && (
            <h3 className='px-3 my-2 text-xs font-semibold uppercase text-muted-foreground tracking-wider'>Main</h3>
          )}
          {mainNavItems.map(item => (
            <NavLink
              key={item.href}
              item={item}
              collapsed={collapsed && !isMobile}
              isActive={item.isActive || false}
              onClick={() => {
                navigate(item.href);
                if (isMobile && setIsMobileOpen) setIsMobileOpen(false);
              }}
            />
          ))}
        </div>

        <div className='space-y-1'>
          {(!collapsed || isMobile) && (
            <h3 className='px-3 mb-2 text-xs font-semibold uppercase text-muted-foreground tracking-wider'>Settings</h3>
          )}
          {settingsNavItems.map(item => (
            <NavLink
              key={item.href}
              item={item}
              collapsed={collapsed && !isMobile}
              isActive={item.isActive || false}
              onClick={() => {
                navigate(item.href);
                if (isMobile && setIsMobileOpen) setIsMobileOpen(false);
              }}
            />
          ))}
        </div>
      </nav>

      <div className={`mt-auto border-t ${collapsed && !isMobile ? 'p-1' : 'p-2'}`}>
        <div className='flex items-center mb-2'>
          {(!collapsed || isMobile) && <span className='text-sm text-muted-foreground ml-3'>Toggle theme</span>}
          <div className='ml-auto mr-2'>
            <ThemeToggle />
          </div>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant='ghost'
              className={`w-full h-9 text-destructive hover:text-destructive ${
                collapsed && !isMobile ? 'px-0 justify-center' : 'px-3 justify-start'
              }`}
            >
              <LogOut className={`h-4 w-4 ${collapsed && !isMobile ? '' : 'mr-2'}`} />
              {(!collapsed || isMobile) && <span>Logout</span>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Logout</AlertDialogTitle>
              <AlertDialogDescription>Are you sure you want to log out of your account?</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLogout}
                className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
              >
                Logout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex lg:flex-col fixed inset-y-0 left-0 z-20 bg-background transition-all duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar using Sheet component */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side='left' className='p-0 w-[280px] sm:max-w-sm border-r'>
          <SidebarContent />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Sidebar;
